import type { SupportedPhotoType } from './types';

/**
 * Reads an image's real format from its first bytes.
 *
 * ⚠️ **This exists because `file.type` is not reliable on a phone.** It is
 * whatever the picker chose to declare, and several common paths declare
 * nothing useful:
 *
 *   Google Photos / Drive-backed pickers  often hand over `''` or
 *                                         `application/octet-stream`
 *   "Share to app" and some file managers  arbitrary or absent
 *   Android download caches                `image/*`
 *
 * The old code took `file.type`, failed to normalise it, and told the customer
 * "that file type is not supported" — for a perfectly ordinary JPEG. Sniffing
 * the bytes turns that into a fact rather than a claim.
 *
 * Mirrors `api/src/services/bucket/media-type.ts`'s `sniff`, deliberately: the
 * server proves garment images the same way, and the two must agree on what
 * each format looks like. Twelve bytes covers all five.
 *
 * ```
 *   FF D8 FF                   jpeg
 *   89 "PNG" 0D 0A 1A 0A       png
 *   "RIFF" ???? "WEBP"         webp   (bytes 0-3 and 8-11)
 *   ???? "ftyp" <brand>        heic / heif   (brand at bytes 8-11)
 * ```
 *
 * Returns null for anything else — including AVIF, which shares the `ftyp`
 * container with HEIC and which Gemini does not accept.
 */
export async function sniffPhotoType(file: File): Promise<SupportedPhotoType | null> {
  let bytes: Uint8Array;
  try {
    bytes = await readHead(file);
  } catch {
    // A file backed by a provider that has since gone away — a cloud picker
    // entry whose download failed. Nothing to read, so nothing to claim.
    return null;
  }

  if (bytes.length < 12) return null;

  const ascii = (start: number, end: number): string =>
    String.fromCharCode(...bytes.subarray(start, end));

  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';

  if (
    bytes[0] === 0x89 &&
    ascii(1, 4) === 'PNG' &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return 'image/png';
  }

  if (ascii(0, 4) === 'RIFF' && ascii(8, 12) === 'WEBP') return 'image/webp';

  if (ascii(4, 8) === 'ftyp') {
    return HEIF_BRANDS.get(ascii(8, 12).toLowerCase()) ?? null;
  }

  return null;
}

/** How many bytes identify a format — 12 covers all five. */
const HEAD_BYTES = 12;

/**
 * The first bytes of a file, via `Blob.arrayBuffer` where it exists and
 * `FileReader` where it does not.
 *
 * ⚠️ **The fallback is not dead code.** `Blob.prototype.arrayBuffer` arrived in
 * Safari **14**, and this app runs on shop iPads that are bought once and kept —
 * on an older one the promise path throws `TypeError`, every photo sniffs as
 * unrecognised, and the customer is told their JPEG is an unsupported format.
 * `FileReader` has been there since forever.
 */
async function readHead(file: File): Promise<Uint8Array> {
  const head = file.slice(0, HEAD_BYTES);

  if (typeof head.arrayBuffer === 'function') {
    return new Uint8Array(await head.arrayBuffer());
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsArrayBuffer(head);
  });
}

/**
 * The brands Gemini's two HEIF types cover. `heic` is the HEVC-coded profile,
 * `heif` the generic one.
 *
 * ⚠️ `avif` and `avis` are deliberately absent — same container, AV1 payload,
 * and Gemini does not list them. Treating "has an `ftyp` box" as "is a HEIC"
 * would send one upstream mislabelled.
 */
const HEIF_BRANDS: ReadonlyMap<string, SupportedPhotoType> = new Map([
  ['heic', 'image/heic'],
  ['heix', 'image/heic'],
  ['hevc', 'image/heic'],
  ['hevx', 'image/heic'],
  ['heim', 'image/heif'],
  ['heis', 'image/heif'],
  ['hevm', 'image/heif'],
  ['hevs', 'image/heif'],
  ['mif1', 'image/heif'],
  ['msf1', 'image/heif'],
]);
